<script lang="ts">
export const BASE_COLOR = "#1d1b20";
</script>

<script setup lang="ts">
import { nextTick, onBeforeUnmount, ref } from "vue";
import { useI18n } from "vue-i18n";
import { ColorPicker } from "vue3-colorpicker";

import TcButton from "@/Components/UI/TcButton.vue";

export interface ColorPickerProps {
  modelValue?: string;
}

const { t } = useI18n();

const props = withDefaults(defineProps<ColorPickerProps>(), { modelValue: BASE_COLOR });

const rootRef = ref<HTMLDivElement | null>(null);
const anchorRef = ref<HTMLDivElement | null>(null);
const panelRef = ref<HTMLDivElement | null>(null);

const isOpen = ref<boolean>(false);
const xAlign = ref<"left" | "right">("left");
const yAlign = ref<"top" | "bottom">("bottom");
const isMobile = ref<boolean>(false);
const mobileMode = ref<"popover" | "sheet">("popover");
const panelStyle = ref<Record<string, string>>({});
let bodyOverflow: string | null = null;
let bodyTouchAction: string | null = null;

const emit = defineEmits<{
  (e: "update:modelValue", value: string): void;
  (e: "change", value: string): void;
}>();

const handleChange = (v: string) => {
  emit("update:modelValue", v);
  emit("change", v);
};

const lockBodyScroll = () => {
  if (bodyOverflow !== null) {
    return;
  }

  bodyOverflow = document.body.style.overflow;
  bodyTouchAction = document.body.style.touchAction;
  document.body.style.overflow = "hidden";
  document.body.style.touchAction = "none";
};

const unlockBodyScroll = () => {
  if (bodyOverflow === null) {
    return;
  }

  document.body.style.overflow = bodyOverflow;
  document.body.style.touchAction = bodyTouchAction ?? "";
  bodyOverflow = null;
  bodyTouchAction = null;
};

const syncBodyScroll = () => {
  if (isOpen.value && mobileMode.value === "sheet") {
    lockBodyScroll();
  } else {
    unlockBodyScroll();
  }
};

const computePlacement = () => {
  if (!isOpen.value) {
    return;
  }

  const anchorEl = anchorRef.value;
  const panelEl = panelRef.value;

  if (!anchorEl || !panelEl) {
    return;
  }

  const viewportW = window.innerWidth;
  const viewportH = window.innerHeight;
  const margin = 8;

  isMobile.value = viewportW <= 768;

  const anchorRect = anchorEl.getBoundingClientRect();
  const panelRect = panelEl.getBoundingClientRect();

  const spaceBottom = viewportH - anchorRect.bottom - margin;
  const spaceTop = anchorRect.top - margin;
  const needsSheet = isMobile.value && spaceBottom < panelRect.height && spaceTop < panelRect.height;

  if (needsSheet) {
    mobileMode.value = "sheet";
    panelStyle.value = {};
    syncBodyScroll();
    return;
  }

  mobileMode.value = "popover";

  const spaceRight = viewportW - anchorRect.left - margin;
  const spaceLeft = anchorRect.right - margin;
  const fitsLeft = panelRect.width <= spaceRight;
  const fitsRight = panelRect.width <= spaceLeft;

  if (fitsLeft) {
    xAlign.value = "left";
  } else if (fitsRight) {
    xAlign.value = "right";
  } else {
    const overflowLeft = panelRect.width - spaceRight;
    const overflowRight = panelRect.width - spaceLeft;
    xAlign.value = overflowLeft <= overflowRight ? "left" : "right";
  }

  const fitsBottom = panelRect.height <= spaceBottom;
  const fitsTop = panelRect.height <= spaceTop;

  if (fitsBottom) {
    yAlign.value = "bottom";
  } else if (fitsTop) {
    yAlign.value = "top";
  } else {
    yAlign.value = spaceBottom >= spaceTop ? "bottom" : "top";
  }

  const left = xAlign.value === "left" ? anchorRect.left : anchorRect.right - panelRect.width;
  const top = yAlign.value === "bottom" ? anchorRect.bottom : anchorRect.top - panelRect.height;

  const maxLeft = Math.max(margin, viewportW - panelRect.width - margin);
  const maxTop = Math.max(margin, viewportH - panelRect.height - margin);
  const clampedLeft = Math.min(Math.max(margin, left), maxLeft);
  const clampedTop = Math.min(Math.max(margin, top), maxTop);

  panelStyle.value = {
    position: "fixed",
    left: `${clampedLeft}px`,
    top: `${clampedTop}px`,
  };

  syncBodyScroll();
};

const handleDocumentClick = (event: MouseEvent | TouchEvent) => {
  const rootEl = rootRef.value;
  const target = event.target as Node | null;

  if (!rootEl || !target) {
    return;
  }

  if (!rootEl.contains(target)) {
    close();
  }
};

const handleKeydown = (event: KeyboardEvent) => {
  if (event.key === "Escape") {
    close();
  }
};

const handleWindowChange = () => {
  computePlacement();
};

const addListeners = () => {
  document.addEventListener("mousedown", handleDocumentClick);
  document.addEventListener("touchstart", handleDocumentClick);
  document.addEventListener("keydown", handleKeydown);
  window.addEventListener("resize", handleWindowChange);
  window.addEventListener("scroll", handleWindowChange, true);
};

const removeListeners = () => {
  document.removeEventListener("mousedown", handleDocumentClick);
  document.removeEventListener("touchstart", handleDocumentClick);
  document.removeEventListener("keydown", handleKeydown);
  window.removeEventListener("resize", handleWindowChange);
  window.removeEventListener("scroll", handleWindowChange, true);
};

const open = async () => {
  if (isOpen.value) {
    return;
  }

  isOpen.value = true;
  panelStyle.value = { position: "fixed", left: "0px", top: "0px", visibility: "hidden" };
  await nextTick();
  computePlacement();
  addListeners();
};

const close = () => {
  if (!isOpen.value) {
    return;
  }

  isOpen.value = false;
  removeListeners();
  unlockBodyScroll();
};

const toggle = () => {
  if (isOpen.value) {
    close();
  } else {
    open();
  }
};

onBeforeUnmount(() => {
  removeListeners();
  unlockBodyScroll();
});
</script>

<template>
  <div ref="rootRef" class="color-picker">
    <div
      ref="anchorRef"
      class="color-picker__button"
      :style="{ background: props.modelValue }"
      @click="toggle"
    />

    <div v-if="isOpen && mobileMode === 'sheet'" class="colorpicker-overlay" @click="close" />

    <div
      v-if="isOpen"
      ref="panelRef"
      class="colorpicker-panel"
      :class="{ 'is-sheet': mobileMode === 'sheet' }"
      :style="panelStyle"
    >
      <ColorPicker
        isWidget
        class="color_picker"
        pickerType="chrome"
        format="hex"
        lang="En"
        disableAlpha
        :pureColor="props.modelValue"
        @update:pureColor="handleChange"
      >
        <template #extra>
          <TcButton class="mt-4" variant="outlined" isSmall @click="close">
            {{ t("documents.editor.color_picker.close.button") }}
          </TcButton>
        </template>
      </ColorPicker>
    </div>
  </div>
</template>

<style scoped lang="scss">
.color-picker {
  height: $space-5 !important;
  width: $space-5 !important;
  cursor: pointer;
  position: relative;

  &__button {
    @include radius(s);

    height: 100%;
    width: 100%;
  }
}

.colorpicker-panel {
  @include radius(m);
  position: fixed;
  z-index: 1100;
  box-shadow: 0 16px 40px rgba(0, 0, 0, 0.2);
}

.colorpicker-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  z-index: 1090;
}

.colorpicker-panel.is-sheet {
  left: 50%;
  bottom: 8px;
  top: auto;
  transform: translateX(-50%);
  max-height: 80vh;
  width: min(calc(100vw - 16px), 360px);
  overflow: auto;
}
</style>
